import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { API_BASE_URL } from '../../constants/system'
import useAccount from '../useAccount'

/** A code a person accepted into a dynamic code frame, and when. */
export interface AddedCode {
  code: string
  text: string
  addedAt: string
}

interface FrameVersion {
  version: number
  createdAt: string
  addedCodes: Array<{ code: string; text: string }>
}

/**
 * The codes accepted into a dynamic library frame since it was created, from
 * its version history (`GET …/frameworks/:id/versions`), oldest first. The Sky
 * dashboards mark them under the opinion charts, so a step in a trend that is
 * really a new code is never read as a change in what people said (D13 of
 * design/2026-10-21-sky-sources-influencer-dynamic-frames.md).
 *
 * The frame is found by name in the job's library; a job with no such frame,
 * or a frame never added to, answers an empty list.
 */
export function useFrameCodeHistory(customer: string, job: string, frameName: string, enabled = true) {
  const account = useAccount()
  return useQuery<AddedCode[]>({
    queryKey: ['frame-code-history', customer, job, frameName],
    enabled: Boolean(customer && job && frameName && account.user && enabled),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const base = `${API_BASE_URL}/autocoder/${customer}/${job}/frameworks`
      const frames = (await axios.get<Array<{ id: string; name: string; dynamic?: boolean }>>(base)).data ?? []
      const frame = frames.find((f) => f.name.toLowerCase() === frameName.toLowerCase() && f.dynamic)
      if (!frame) return []
      const versions = (await axios.get<FrameVersion[]>(`${base}/${frame.id}/versions`)).data ?? []
      return versions
        .flatMap((v) => (v.addedCodes ?? []).map((c) => ({ code: c.code, text: c.text, addedAt: v.createdAt })))
        .sort((a, b) => a.addedAt.localeCompare(b.addedAt))
    },
  })
}

/** "Wants translation (from 21 Oct 2026)" — one phrase per added code. */
export function describeAddedCodes(codes: AddedCode[]): string {
  return codes
    .map((c) => {
      const d = new Date(c.addedAt)
      const day = Number.isNaN(d.getTime())
        ? c.addedAt
        : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
      return `${c.text} (from ${day})`
    })
    .join(', ')
}
